/**
 * GR 문항 자료 묶음(학습팀 최종본 JSON) 검사·정규화(v0.1.6, 총괄 DEV-I-005 항목 1).
 *
 * - 자료 묶음은 외부 ID(문항·지점·보기·정답 기준·해설 등)와 버전, 자료에 적힌 승인 기록 ID를 담는다.
 *   여기서는 형식과 서로의 일관성만 검사한다. 자료에 적힌 승인 기록 ID·상태 문구·release_allowed는
 *   "주장"으로만 보존하며 승인을 부여하지 않는다(승인 연결은 권한 있는 서버 처리).
 * - 같은 외부 문항 ID·콘텐츠 버전의 재적재는 contentFingerprint가 같을 때만 같은 자료로 본다.
 *   meta(상태 문구·날짜·release_allowed 표시값)는 비교에서 뺀다. 내용이 다르면 서버가 거부한다.
 */
import { canonicalJson } from './idempotency.js';
const ID = /^[A-Za-z0-9_-]{1,64}$/;
const VER = /^v[1-9][0-9]*$/;
const MARKER = /\{\{([A-Za-z0-9_-]{1,64})\}\}/g;
const str = (v) => typeof v === 'string' && v.length > 0;
const optStr = (v) => (typeof v === 'string' && v.length > 0 ? v : null);
const obj = (v) => (v !== null && typeof v === 'object' && !Array.isArray(v) ? v : null);
/** 외부 버전 문자열 'v3' → 3. 형식이 아니면 null */
export function versionNumber(v) {
    return VER.test(v) ? Number(v.slice(1)) : null;
}
export function parseGrPackage(raw) {
    const errors = [];
    const root = obj(raw);
    const item = obj(root?.['item']);
    if (root === null || item === null)
        return { ok: false, errors: ['package_malformed'] };
    const itemId = item['item_id'];
    const contentVersion = item['content_version'];
    const stemMarked = item['stem_marked'];
    if (!str(itemId) || !ID.test(itemId))
        errors.push('item_id_invalid');
    if (!str(contentVersion) || !VER.test(contentVersion))
        errors.push('content_version_invalid');
    if (!str(stemMarked))
        errors.push('stem_marked_missing');
    const rawPoints = root['points'];
    if (!Array.isArray(rawPoints) || rawPoints.length === 0)
        errors.push('points_missing');
    const points = [];
    const seenPoint = new Set();
    for (const [pi, rp] of (Array.isArray(rawPoints) ? rawPoints : []).entries()) {
        const p = obj(rp);
        const at = `points[${pi}]`;
        if (p === null) {
            errors.push(`${at}:malformed`);
            continue;
        }
        const pointId = p['point_id'];
        if (!str(pointId) || !ID.test(pointId)) {
            errors.push(`${at}:point_id_invalid`);
            continue;
        }
        if (seenPoint.has(pointId))
            errors.push(`${pointId}:point_duplicate`);
        seenPoint.add(pointId);
        const screen = p['screen_number'];
        if (typeof screen !== 'number' || !Number.isInteger(screen) || screen < 1)
            errors.push(`${pointId}:screen_number_invalid`);
        const opts = [];
        const rawOpts = Array.isArray(p['options']) ? p['options'] : [];
        for (const ro of rawOpts) {
            const o = obj(ro);
            const oid = o?.['option_id'];
            const text = o?.['text'];
            if (!str(oid) || !ID.test(oid) || !oid.startsWith(`${pointId}-`)) {
                errors.push(`${pointId}:option_id_invalid`);
                continue;
            }
            if (!str(text)) {
                errors.push(`${oid}:option_text_missing`);
                continue;
            }
            opts.push({ optionId: oid, text });
        }
        if (opts.length < 2)
            errors.push(`${pointId}:too_few_options`);
        if (new Set(opts.map((o) => o.optionId)).size !== opts.length)
            errors.push(`${pointId}:option_id_duplicate`);
        if (new Set(opts.map((o) => o.text)).size !== opts.length)
            errors.push(`${pointId}:option_text_duplicate`);
        const ak = obj(p['answer_key']);
        const akVersion = ak?.['version'];
        const accepted = Array.isArray(ak?.['accepted_option_ids']) ? ak['accepted_option_ids'] : null;
        if (ak === null || !str(akVersion) || !VER.test(akVersion))
            errors.push(`${pointId}:answer_key_version_invalid`);
        if (accepted === null || accepted.length === 0)
            errors.push(`${pointId}:answer_key_empty`);
        else {
            if (!accepted.every((a) => typeof a === 'string' && opts.some((o) => o.optionId === a)))
                errors.push(`${pointId}:answer_key_foreign_option`);
            if (new Set(accepted).size !== accepted.length)
                errors.push(`${pointId}:answer_key_duplicate`);
        }
        const components = [];
        for (const kind of ['explanation', 'evidence_question', 'evidence_span']) {
            const c = obj(p[kind]);
            if (c === null)
                continue;
            if (!str(c['id']) || !ID.test(c['id']) || !str(c['version']) || !VER.test(c['version'])) {
                errors.push(`${pointId}:${kind}_id_invalid`);
                continue;
            }
            components.push({ kind, id: c['id'], version: c['version'], approvalRecord: optStr(c['approval_record']), body: c });
        }
        points.push({
            pointId,
            screenNumber: typeof screen === 'number' ? screen : 0,
            options: opts,
            answerKey: { version: str(akVersion) ? akVersion : '', acceptedOptionIds: (accepted ?? []).filter((a) => typeof a === 'string'), approvalRecord: optStr(ak?.['approval_record']) },
            components,
            wordLinks: Array.isArray(p['word_links']) ? p['word_links'] : [],
        });
    }
    // 지문 위치 표지와 지점: 지점마다 정확히 한 번, 모르는 표지 없음
    if (str(stemMarked)) {
        const marks = [...stemMarked.matchAll(MARKER)].map((m) => m[1]);
        const opens = stemMarked.split('{{').length - 1;
        if (opens !== marks.length || stemMarked.split('}}').length - 1 !== marks.length)
            errors.push('stem_marker_malformed');
        for (const p of points) {
            const n = marks.filter((k) => k === p.pointId).length;
            if (n === 0)
                errors.push(`${p.pointId}:marker_missing`);
            if (n > 1)
                errors.push(`${p.pointId}:marker_duplicated`);
        }
        for (const k of new Set(marks))
            if (!seenPoint.has(k))
                errors.push(`${k}:marker_unknown`);
        // 지점 순서는 지문 표지 순서와 같아야 한다(서버 release_structure 규칙과 같은 기준)
        const order = marks.filter((k) => seenPoint.has(k));
        if (errors.length === 0 && order.join('|') !== points.map((p) => p.pointId).join('|'))
            errors.push('point_order_mismatch');
    }
    if (errors.length > 0)
        return { ok: false, errors };
    return {
        ok: true,
        pkg: {
            itemId: itemId, contentVersion: contentVersion, approvalRecord: optStr(item['approval_record']),
            derivedFrom: optStr(item['derived_from']), passageId: optStr(item['passage_id']), stemMarked: stemMarked,
            sourceLocation: optStr(item['source_location']), points,
        },
    };
}
/** 재적재 비교용 내용 문자열: meta를 뺀 묶음 전체(키 정렬). 서버가 해시로 저장한다. */
export function contentFingerprintSource(raw) {
    const o = obj(raw) ?? {};
    const rest = Object.fromEntries(Object.entries(o).filter(([k]) => k !== 'meta'));
    return canonicalJson(rest);
}
